import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useConfig } from '../../../contexts/ConfigContext';
import { usePagePermission } from '../../../hooks/usePagePermission';
import AccessDenied from '../../../components/AccessDenied';
import DataTable from '../../../components/DataTable';
import type { Column, Filter, HeaderActions } from '../../../components/DataTable';
import type { ServiceLog } from '../../../types';
import EmailWithStatus from '../../../components/EmailWithStatus';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending', draft: 'Draft', sent: 'Sent',
  overdue: 'Overdue', paid: 'Paid', void: 'Void',
};

// Column accent colors for Kanban board headers
const STATUS_COLORS: Record<string, string> = {
  pending: 'var(--color-warning-text)', draft: 'var(--color-text-light)', sent: 'var(--color-info)',
  overdue: 'var(--color-error)', paid: 'var(--color-success)',
};

// Workflow columns for Kanban board
const WORKFLOW_COLUMNS = [
  { id: 'pending', label: 'Pending' },
  { id: 'draft', label: 'Draft' },
  { id: 'sent', label: 'Sent' },
  { id: 'overdue', label: 'Overdue' },
  { id: 'paid', label: 'Paid' },
];

type SortType = 'date_desc' | 'date_asc' | 'customer_asc' | 'customer_desc' | 'amount_desc' | 'amount_asc';

function createSortFn(sortType: SortType) {
  return (a: Record<string, unknown>, b: Record<string, unknown>) => {
    switch (sortType) {
      case 'date_asc':
        return new Date(a.service_date as string).getTime() - new Date(b.service_date as string).getTime();
      case 'customer_asc':
        return ((a.customer_name as string) || '').localeCompare((b.customer_name as string) || '');
      case 'customer_desc':
        return ((b.customer_name as string) || '').localeCompare((a.customer_name as string) || '');
      case 'amount_desc':
        return ((b.total_amount as number) || 0) - ((a.total_amount as number) || 0);
      case 'amount_asc':
        return ((a.total_amount as number) || 0) - ((b.total_amount as number) || 0);
      case 'date_desc':
      default:
        return new Date(b.service_date as string).getTime() - new Date(a.service_date as string).getTime();
    }
  };
}

/** boardOnly: the Dashboard's Service Logs tab shows the board alone, without the List / Board toggle. */
export default function ServiceLogsPage({ boardOnly = false }: { boardOnly?: boolean } = {}) {
  const navigate = useNavigate();
  const { formatCurrency, formatDate } = useConfig();
  const { canAccess, isLoading: permissionLoading } = usePagePermission('service_logs');
  const [logs, setLogs] = useState<ServiceLog[]>([]);
  const [loading, setLoading] = useState(true);

  // View modal state
  const [viewingLog, setViewingLog] = useState<Record<string, unknown> | null>(null);

  // View mode: 'list' or 'card' — saved to localStorage
  const [viewMode, setViewMode] = useState<'list' | 'card'>(() => {
    if (boardOnly) return 'card';
    return (localStorage.getItem('serviceLogsViewMode') as 'list' | 'card') || 'list';
  });

  // Card view search and filter state
  const [cardSearch, setCardSearch] = useState('');

  // Per-column sort state
  const [columnSorts, setColumnSorts] = useState<Record<string, SortType>>(() => {
    try {
      const saved = localStorage.getItem('serviceLogsColumnSorts');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Save preferences
  useEffect(() => {
    if (!boardOnly) localStorage.setItem('serviceLogsViewMode', viewMode);
  }, [viewMode, boardOnly]);

  useEffect(() => {
    localStorage.setItem('serviceLogsColumnSorts', JSON.stringify(columnSorts));
  }, [columnSorts]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_logs')
        .select('*, customers(customer_name, email, customer_type, is_pos, email_bouncing, email_last_bounce_at), territories(name)')
        .order('service_date', { ascending: false })
        .limit(10000);

      if (error) {
        console.error('Failed to load service logs:', error.message);
      } else {
        setLogs((data ?? []) as ServiceLog[]);
      }
      setLoading(false);
    }
    load();
  }, []);

  // Group logs by status for Kanban board
  const logsByStatus = useMemo(() => {
    let filtered = logs as unknown as Record<string, unknown>[];

    if (cardSearch) {
      const search = cardSearch.toLowerCase();
      filtered = filtered.filter((log) => {
        const customer = log.customers as { customer_name?: string; email?: string; email_bouncing?: boolean; email_last_bounce_at?: string | null } | null;
        return (
          customer?.customer_name?.toLowerCase().includes(search) ||
          customer?.email?.toLowerCase().includes(search) ||
          (log.service_log_number as string)?.toLowerCase().includes(search)
        );
      });
    }

    const grouped: Record<string, Record<string, unknown>[]> = {};
    WORKFLOW_COLUMNS.forEach((col) => { grouped[col.id] = []; });
    filtered.forEach((log) => {
      const statusKey = log.status as string;
      const bucket = grouped[statusKey];
      if (bucket) {
        bucket.push(log);
      }
    });

    Object.keys(grouped).forEach((colId) => {
      const sortType = (columnSorts[colId] || 'date_desc') as SortType;
      grouped[colId]!.sort(createSortFn(sortType));
    });

    return grouped;
  }, [logs, cardSearch, columnSorts]);

  const columns: Column[] = [
    {
      key: 'service_log_number',
      label: '#',
      sortable: true,
      width: '90px',
      hideOnMobile: true,
      render: (row) => (
        <span className="ref-code">{(row.service_log_number as string) ?? '-'}</span>
      ),
    },
    {
      key: 'customers.customer_name',
      label: 'Customer',
      sortable: true,
      render: (row) => {
        const customer = row.customers as { customer_name?: string; email?: string; email_bouncing?: boolean; email_last_bounce_at?: string | null } | null;
        return (
          <div>
            <div className="table-cell-name">{customer?.customer_name ?? '-'}</div>
            {customer?.email ? (
              <div className="table-cell-description">
                <EmailWithStatus
                  email={customer.email}
                  isBouncing={customer.email_bouncing}
                  lastBounceAt={customer.email_last_bounce_at}
                  compact
                />
              </div>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'territories.name',
      label: 'Territory',
      sortable: true,
      hideOnMobile: true,
      render: (row) => {
        const territory = row.territories as { name?: string } | null;
        return territory?.name ? (
          <span className="code-chip">{territory.name}</span>
        ) : (
          <span className="text-muted text-xs">-</span>
        );
      },
    },
    {
      key: 'service_date',
      label: 'Date',
      sortable: true,
      hideOnMobile: true,
      render: (row) => formatDate(row.service_date as string),
    },
    {
      key: 'total_amount',
      label: 'Total',
      sortable: true,
      align: 'right',
      render: (row) => (
        <strong>{formatCurrency(row.total_amount as number)}</strong>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => {
        const s = row.status as string;
        const pillClass = s === 'void' ? 'voided' : s;
        return (
          <span className={`status-pill status-pill--${pillClass}`}>
            <span className="status-pill-dot" style={{ background: 'currentColor' }} />
            {STATUS_LABELS[s] || s}
          </span>
        );
      },
    },
  ];

  const filters: Filter[] = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'unpaid', label: 'Unpaid', matchValues: ['sent', 'overdue'] },
        { value: 'pending', label: 'Pending' },
        { value: 'draft', label: 'Draft' },
        { value: 'sent', label: 'Sent' },
        { value: 'paid', label: 'Paid' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'void', label: 'Void' },
      ],
    },
  ];

  const headerActions: HeaderActions = {
    primary: {
      label: '+ New Service Log',
      onClick: () => navigate('/service-logs/new'),
    },
  };

  if (permissionLoading) return null;
  if (!canAccess) return <AccessDenied feature="service_logs" />;

  return (
    <div>
      {/* Page Tabs — List / Card toggle (matches Global) */}
      {!boardOnly && <div className="page-tabs">
        <button
          className={`page-tab ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
        >
          List
        </button>
        <button
          className={`page-tab ${viewMode === 'card' ? 'active' : ''}`}
          onClick={() => setViewMode('card')}
        >
          Board
        </button>
      </div>}

      {viewMode === 'list' ? (
        <DataTable
          columns={columns}
          data={logs as unknown as Record<string, unknown>[]}
          loading={loading}
          searchable
          searchPlaceholder="Search customers, emails, log number..."
          searchKeys={['customers.customer_name', 'customers.email', 'service_log_number', 'subject']}
          filters={filters}
          defaultSort={{ key: 'service_date', direction: 'desc' }}
          headerActions={headerActions}
          onRowClick={(row) => setViewingLog(row)}
          actions={(row) => {
            const isPending = row.status === 'pending';
            return (
              <>
                <span className="action-icon" onClick={(e) => { e.stopPropagation(); setViewingLog(row); }} title="View">👁️</span>
                {isPending && (
                  <span className="action-icon" onClick={(e) => { e.stopPropagation(); navigate(`/service-logs/${row.id as string}/edit`); }} title="Edit">✏️</span>
                )}
              </>
            );
          }}
          emptyMessage="No service logs found."
          pageSize={50}
          idKey="id"
        />
      ) : (
        <>
          {/* Card View Toolbar */}
          <div className="list-toolbar">
            <div className="list-toolbar-left">
              <div className="list-search">
                <span className="list-search-icon">{'\ud83d\udd0d'}</span>
                <input
                  type="text"
                  placeholder="Search customer, log #..."
                  value={cardSearch}
                  onChange={(e) => setCardSearch(e.target.value)}
                />
                {cardSearch && (
                  <button className="list-search-clear" onClick={() => setCardSearch('')}>{'\u2715'}</button>
                )}
              </div>
            </div>
            <div className="list-toolbar-right">
              <button className="btn btn-primary" onClick={() => navigate('/service-logs/new')}>
                + New Service Log
              </button>
            </div>
          </div>

          {/* Kanban Board */}
          {loading ? (
            <div className="placeholder-page"><div className="spinner" /></div>
          ) : (
            <BoardView
              logsByStatus={logsByStatus}
              columnSorts={columnSorts}
              onColumnSortChange={(colId: string, sortType: SortType) => {
                setColumnSorts((prev) => ({ ...prev, [colId]: sortType }));
              }}
              onView={(log: Record<string, unknown>) => setViewingLog(log)}
              onEdit={(log: Record<string, unknown>) => navigate(`/service-logs/${log.id as string}/edit`)}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
            />
          )}
        </>
      )}

      {/* View Service Log Modal */}
      {viewingLog ? (
        <ViewServiceLogModal
          log={viewingLog}
          onClose={() => setViewingLog(null)}
          onEdit={() => {
            const logId = viewingLog.id as string;
            setViewingLog(null);
            navigate(`/service-logs/${logId}/edit`);
          }}
          onViewFull={() => {
            const logId = viewingLog.id as string;
            setViewingLog(null);
            navigate(`/service-logs/${logId}`);
          }}
          formatCurrency={formatCurrency}
          formatDate={formatDate}
        />
      ) : null}
    </div>
  );
}

// View Service Log Modal — universal styling
function ViewServiceLogModal({
  log,
  onClose,
  onEdit,
  onViewFull,
  formatCurrency,
  formatDate,
}: {
  log: Record<string, unknown>;
  onClose: () => void;
  onEdit: () => void;
  onViewFull: () => void;
  formatCurrency: (amount: number) => string;
  formatDate: (date: string) => string;
}) {
  const customer = log.customers as { customer_name?: string; email?: string; email_bouncing?: boolean; email_last_bounce_at?: string | null } | null;
  const territory = log.territories as { name?: string } | null;
  const isPending = log.status === 'pending';
  const statusPill = log.status === 'void' ? 'voided' : (log.status as string);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
        <div className="modal-header">
          <div>
            <h2 style={{ margin: 0, fontSize: 'var(--text-2xl)' }}>
              Service Log {(log.service_log_number as string) ?? ''}
            </h2>
            <span
              className={`status-pill status-pill--${statusPill} mt-xs inline-flex`}
            >
              <span className="status-pill-dot" style={{ background: 'currentColor' }} />
              {STATUS_LABELS[log.status as string] || (log.status as string)}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {/* Customer & Service */}
          <div className="view-section">
            <div className="section-title">Service Details</div>
            <div className="view-row">
              <span className="view-label">Customer</span>
              <span className="view-value fw-600">{customer?.customer_name ?? '-'}</span>
            </div>
            {customer?.email && (
              <div className="view-row">
                <span className="view-label">Email</span>
                <span className="view-value">
                  <EmailWithStatus
                    email={customer.email}
                    isBouncing={customer.email_bouncing}
                    lastBounceAt={customer.email_last_bounce_at}
                  />
                </span>
              </div>
            )}
            <div className="view-row">
              <span className="view-label">Date</span>
              <span className="view-value">{formatDate(log.service_date as string)}</span>
            </div>
            {Boolean(log.service_location) && (
              <div className="view-row">
                <span className="view-label">Location</span>
                <span className="view-value">{log.service_location as string}</span>
              </div>
            )}
            {territory?.name && (
              <div className="view-row">
                <span className="view-label">Territory</span>
                <span className="view-value">
                  <span className="code-chip">{territory.name}</span>
                </span>
              </div>
            )}
          </div>

          {/* Financials */}
          <div className="view-section">
            <div className="section-title">Financials</div>
            <div className="view-row">
              <span className="view-label">Subtotal</span>
              <span className="view-value">{formatCurrency(log.subtotal as number)}</span>
            </div>
            {(log.discount_amount as number) > 0 && (
              <div className="view-row">
                <span className="view-label">Discount</span>
                <span className="view-value text-error">
                  -{formatCurrency(log.discount_amount as number)}
                </span>
              </div>
            )}
            <div className="view-row">
              <span className="view-label">
                VAT ({((log.vat_rate as number) * 100).toFixed(0)}%)
                {Boolean(log.vat_inclusive) && ' incl.'}
              </span>
              <span className="view-value">{formatCurrency(log.vat_amount as number)}</span>
            </div>
            <div className="view-row" style={{ borderTop: '1px solid var(--color-border)', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
              <span className="view-label" style={{ fontWeight: 600, color: 'var(--color-text)' }}>Total</span>
              <span className="view-value font-bold text-base">
                {formatCurrency(log.total_amount as number)}
              </span>
            </div>
          </div>

          {/* Internal Notes */}
          {Boolean(log.internal_notes) && (
            <div className="view-section" style={{ borderBottom: 'none' }}>
              <div className="section-title">Internal Notes</div>
              <p style={{ fontSize: 'var(--text-base)', margin: 0, color: 'var(--color-text)' }}>
                {log.internal_notes as string}
              </p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className={`btn-grid w-full${isPending ? ' btn-grid--3' : ''}`}>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
            <button className="btn btn-secondary" onClick={onViewFull}>Full Details</button>
            {isPending && (
              <button className="btn btn-primary" onClick={onEdit}>Edit</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Kanban Board View Component — matches Global exactly
function BoardView({
  logsByStatus,
  columnSorts,
  onColumnSortChange,
  onView,
  onEdit,
  formatCurrency,
  formatDate,
}: {
  logsByStatus: Record<string, Record<string, unknown>[]>;
  columnSorts: Record<string, SortType>;
  onColumnSortChange: (colId: string, sortType: SortType) => void;
  onView: (log: Record<string, unknown>) => void;
  onEdit: (log: Record<string, unknown>) => void;
  formatCurrency: (amount: number) => string;
  formatDate: (date: string) => string;
}) {
  return (
    <div className="ops-board">
      {WORKFLOW_COLUMNS.map((col) => {
        const colLogs = logsByStatus[col.id] || [];
        const accentColor = STATUS_COLORS[col.id] ?? 'var(--color-text-light)';
        const currentSort = columnSorts[col.id] || 'date_desc';

        return (
          <div key={col.id} className="ops-board-column">
            <div className="ops-board-column-header" style={{ borderTopColor: accentColor }}>
              <div className="ops-board-column-title-group">
                <span className="ops-board-column-title">{col.label}</span>
                <span className="ops-board-column-count">{colLogs.length}</span>
              </div>
              <select
                className="ops-board-column-sort"
                value={currentSort}
                onChange={(e) => onColumnSortChange(col.id, e.target.value as SortType)}
              >
                <optgroup label="Sort">
                  <option value="date_desc">Newest</option>
                  <option value="date_asc">Oldest</option>
                  <option value="amount_desc">Amount {'\u2193'}</option>
                  <option value="amount_asc">Amount {'\u2191'}</option>
                  <option value="customer_asc">Customer A-Z</option>
                  <option value="customer_desc">Customer Z-A</option>
                </optgroup>
              </select>
            </div>
            <div className="ops-board-column-body">
              {colLogs.length === 0 ? (
                <div className="ops-board-empty">No items</div>
              ) : (
                colLogs.slice(0, 20).map((log) => {
                  const customer = log.customers as { customer_name?: string; email?: string; email_bouncing?: boolean; email_last_bounce_at?: string | null } | null;
                  const territory = log.territories as { name?: string } | null;
                  const isPending = log.status === 'pending';
                  const isPaid = log.status === 'paid';

                  return (
                    <div key={log.id as string} className="ops-board-card" onClick={() => onView(log)}>
                      <div className="ops-board-card-header">
                        <span className="ops-board-card-customer">
                          {customer?.customer_name || 'Unknown'}
                        </span>
                        <span className="ops-board-card-amount">
                          {formatCurrency((log.total_amount as number) || 0)}
                        </span>
                      </div>
                      <div className="ops-board-card-meta">
                        <span>{formatDate(log.service_date as string)}</span>
                        {territory?.name ? (
                          <span className="ops-split-chip-sm">{territory.name}</span>
                        ) : null}
                      </div>
                      {!isPaid && (
                        <div className="ops-board-card-actions" onClick={(e) => e.stopPropagation()}>
                          {isPending ? (
                            <button
                              className="ops-board-action"
                              onClick={(e) => { e.stopPropagation(); onEdit(log); }}
                            >
                              Edit
                            </button>
                          ) : null}
                          <button
                            className="ops-board-action"
                            onClick={(e) => { e.stopPropagation(); onView(log); }}
                          >
                            View
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              {colLogs.length > 20 && (
                <div className="ops-board-more">+{colLogs.length - 20} more</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
